import React, { useEffect, useState } from 'react';
import axios from 'axios';
import host from '../../global.jsx';
import Swal from 'sweetalert2';
import { Check, Code, Keyboard, Mic, PlayCircle } from 'lucide-react';
import QuickDemoLinkModal from './QuickDemoLinkModal.jsx';

const RoundRow = ({ icon, label, description, checked, onChange, alwaysOn = false }) => (
  <div
    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
      alwaysOn
        ? 'bg-purple-500/10 border-purple-400/20'
        : 'bg-white/5 border-white/5'
    }`}
  >
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${alwaysOn ? 'bg-purple-500/20' : 'bg-white/5'}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-[#D3B4FF] font-medium">{label}</p>
        {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
      </div>
    </div>
    {alwaysOn ? (
      <span className="flex items-center gap-1.5 text-xs text-gray-400">
        <Check className="w-3.5 h-3.5 text-purple-300" strokeWidth={2.5} />
        Always included
      </span>
    ) : (
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`w-11 h-6 rounded-full transition-colors relative flex-shrink-0 ${
          checked ? 'bg-gradient-to-r from-pink-500 to-blue-500' : 'bg-gray-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </button>
    )}
  </div>
);

const QuickDemoInterviewContent = () => {
  const accessToken = sessionStorage.getItem('accessToken');

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const [codingEnabled, setCodingEnabled] = useState(false);
  const [typingEnabled, setTypingEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [starting, setStarting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${host}/demo/templates`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setTemplates(res.data || []);
        if (res.data && res.data.length > 0) setSelectedKey(res.data[0].template_key);
      } catch (error) {
        console.error('Failed to load demo templates:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || 'Failed to load demo templates.',
        });
      } finally {
        setTemplatesLoading(false);
      }
    };
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async () => {
    if (!selectedKey) {
      Swal.fire({ icon: 'warning', title: 'Pick a role', text: 'Select a demo role template first.' });
      return;
    }

    setStarting(true);
    try {
      const rounds = [];
      if (codingEnabled) rounds.push('coding');
      if (typingEnabled) rounds.push('typing');

      const res = await axios.post(
        `${host}/demo/quick-start`,
        {
          template_key: selectedKey,
          rounds,
          send_to_email: email?.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setResult({ link: res.data.interview_link, emailedTo: res.data.emailed_to });
    } catch (error) {
      console.error('Quick Start Demo Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to start demo interview.',
      });
    } finally {
      setStarting(false);
    }
  };

  return (
    <div
      className="p-8 rounded-xl max-w-4xl mx-auto mt-10 shadow-2xl"
      style={{
        background: 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(50px)',
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <div className="flex items-center gap-4 mb-8">
        <div className="p-3 bg-purple-500/20 rounded-xl">
          <PlayCircle className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-2xl font-light text-white">Quick Start Demo Interview</h2>
          <p className="text-sm text-gray-400">Start a real interview in one click for client demos</p>
        </div>
      </div>

      <div className="space-y-8">
        <div className="p-6 rounded-xl bg-white/5 border border-white/5">
          <label className="block text-sm text-[#D3B4FF] font-medium mb-4">Role Template</label>
          {templatesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="min-h-[64px] p-5 rounded-xl border border-white/10 bg-[#0a1733] animate-pulse"
                >
                  <div className="h-4 w-3/4 rounded bg-white/10" />
                </div>
              ))}
            </div>
          ) : templates.length === 0 ? (
            <p className="text-sm text-gray-400">No demo templates found. Seed them via Scripts/seed_demo_templates.py.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {templates.map((t) => {
                const isSelected = selectedKey === t.template_key;
                return (
                  <button
                    key={t.template_key}
                    type="button"
                    onClick={() => setSelectedKey(t.template_key)}
                    className={`relative flex items-center min-h-[64px] p-5 rounded-xl border text-left transition-all ${
                      isSelected
                        ? 'border-purple-400 bg-purple-500/15 shadow-lg shadow-purple-500/10'
                        : 'border-white/10 bg-[#0a1733] hover:border-white/30 hover:bg-white/[0.03]'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-gradient-to-r from-pink-500 to-blue-500 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                    <p className="text-sm text-white font-medium pr-6">{t.display_name}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm text-[#D3B4FF] font-medium mb-4">Interview Rounds</label>
          <div className="space-y-3">
            <RoundRow
              icon={<Mic className="w-4 h-4 text-purple-300" />}
              label="Interview"
              description="Questions, follow-ups, and full conversation flow"
              alwaysOn
            />
            <RoundRow
              icon={<Code className="w-4 h-4 text-[#D3B4FF]" />}
              label="Coding Round"
              description="Adds a coding round before the interview"
              checked={codingEnabled}
              onChange={setCodingEnabled}
            />
            <RoundRow
              icon={<Keyboard className="w-4 h-4 text-[#D3B4FF]" />}
              label="Typing Round"
              description="Adds a typing test round before the interview"
              checked={typingEnabled}
              onChange={setTypingEnabled}
            />
          </div>
        </div>

        <div className="p-6 rounded-xl bg-white/5 border border-white/5">
          <label className="block text-sm text-[#D3B4FF] font-medium mb-2">Send Link To (optional)</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="candidate-inbox@example.com"
            className="w-full px-4 py-3 bg-[#0a1733] border border-[#5A4479] rounded-lg text-[#D3B4FF] text-sm focus:outline-none focus:border-purple-500/50 transition-colors placeholder-gray-500"
          />
          <p className="text-xs text-gray-400 mt-3 leading-relaxed">
            Leave this blank to just copy the link below. Nothing gets emailed unless you enter an address here.
          </p>
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleStart}
            disabled={starting || templatesLoading}
            className={`px-10 py-3 rounded-full font-medium text-white transition-all transform active:scale-95 ${
              starting || templatesLoading
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-pink-500 to-blue-500 hover:opacity-90 shadow-lg shadow-pink-500/20'
            }`}
          >
            {starting ? 'Starting...' : 'Start Demo Interview'}
          </button>
        </div>
      </div>

      {result && (
        <QuickDemoLinkModal
          link={result.link}
          emailedTo={result.emailedTo}
          onClose={() => setResult(null)}
        />
      )}
    </div>
  );
};

export default QuickDemoInterviewContent;
